import { assign, fromPromise, setup } from 'xstate';
import { showAxiosErrorAction } from '~/features/actions';
import { getPendingRequestConflict } from '~/features/utils/pendingRequestConflict';
import {
  clearPendingRef,
  handleModal,
  loadingFalse,
  loadingTrue,
  loadPdf,
  openDeleteConfirmation,
  openResendConfirmation,
  setPeerReferences,
  setPendingRef,
  setProviderId,
  setRequestEmail,
  showRequestResentAlert,
  submitError,
} from './actions';
import { REFERENCE_API_CALLS, REFERENCES_API_CALLS } from './api';
import {
  Events,
  OnDeleteRequest,
  OnGetReferencePDF,
  OnGetReferences,
  OnResendReference,
  OnSendRequest,
} from './events';
import { Context, Scope } from './types';
export const peerRefReqMachine = setup({
  actors: {
    sendRequest: fromPromise(REFERENCES_API_CALLS.create),
    getReferences: fromPromise(REFERENCES_API_CALLS.get),
    deleteReference: fromPromise(REFERENCE_API_CALLS.delete),
    resendReference: fromPromise(REFERENCE_API_CALLS.resend),
    getPDF: fromPromise(REFERENCE_API_CALLS.get),
  },
  types: {
    context: {} as Context,
    events: {} as Events,
    input: {} as Partial<Context>,
  },
  actions: {
    handleModal: assign(handleModal),
    setProviderId: assign(setProviderId),
    sendError: showAxiosErrorAction,
    loadingTrue: assign(loadingTrue),
    loadingFalse: assign(loadingFalse),
    populateRefs: assign(setPeerReferences),
    loadPDF: loadPdf,
    submitError: submitError,
    sendDeleteConfirmation: openDeleteConfirmation,
    sendResendConfirmation: openResendConfirmation,
    setRequestEmail: assign(setRequestEmail),
    setPendingRef: assign(setPendingRef),
    clearPendingRef: assign(clearPendingRef),
    showResentAlert: showRequestResentAlert,
  },
  guards: {
    isPendingConflict: ({ event }) =>
      getPendingRequestConflict((event as { error?: unknown }).error) !== null,
  },
}).createMachine({
  context: ({ input }) => ({
    loading: false,
    modal: false,
    peerRefs: input.peerRefs || [],
    providerId: input.providerId || -1,
    sort: {
      sortBy: 'id',
      dateSubmitted: -1,
      lastName: -1,
      referenceDate: -1,
      requestDate: -1,
      id: 1,
      page: 1,
      pageSize: 1,
    },
  }),
  initial: '$_VIEW',
  on: {
    ON_SET_PROVIDER_ID: {
      actions: ['setProviderId'],
    },
    ON_CLOSE_MODAL: {
      actions: ['handleModal'],
      target: '.$_VIEW',
    },
    ON_SUBMIT_ERROR: {
      actions: ['submitError'],
    },
    ON_LOAD: {
      actions: ['loadingTrue'],
      target: '.$_LOAD',
    },
  },
  states: {
    $_VIEW: {
      on: {
        ON_ADD_REQUEST: {
          actions: ['handleModal'],
          target: '$_FORM',
        },
        ON_GET_REFERENCE: {
          target: '$_DOWNLOAD_PDF',
        },
        // MCV-1194: UI dispatches ON_SEND_DELETE_CONFIRMATION which opens a
        // confirmation dialog; the dialog's onConfirm fires ON_DELETE_REFERENCE
        // back into this machine.
        ON_SEND_DELETE_CONFIRMATION: {
          actions: ['sendDeleteConfirmation'],
        },
        ON_DELETE_REFERENCE: {
          actions: ['loadingTrue'],
          target: '$_DELETE',
        },
        // [MCV-1838] Row "Resend": same shape as delete -- the click opens a
        // confirmation whose onConfirm fires ON_RESEND_REFERENCE.
        ON_SEND_RESEND_CONFIRMATION: {
          actions: ['sendResendConfirmation'],
        },
        ON_RESEND_REFERENCE: {
          actions: ['loadingTrue'],
          target: '$_RESEND_ROW',
        },
      },
    },
    $_FORM: {
      on: {
        ON_SEND_REQUEST: {
          target: '$_SEND_REQ',
          actions: ['loadingTrue', 'setRequestEmail'],
        },
      },
    },
    $_SEND_REQ: {
      invoke: {
        id: 'sendRequest',
        src: 'sendRequest',
        input: ({ context, event }) =>
          ({ context, event }) as { context: Context; event: OnSendRequest },
        onDone: {
          target: '$_LOAD',
          actions: ['handleModal'],
        },
        onError: [
          {
            // [MCV-1814] A request to this email is already pending: nothing
            // was created or sent. Keep the dialog open and ask whether to
            // resend that one.
            guard: 'isPendingConflict',
            target: '$_PENDING_EXISTS',
            actions: ['loadingFalse', 'setPendingRef'],
          },
          {
            target: '$_VIEW',
            actions: ['handleModal', 'loadingFalse', 'sendError'],
          },
        ],
      },
    },
    // [MCV-1814] "A pending request already exists for {email}. Resend it?"
    // Resend re-sends that request (same link); Cancel returns to the form with
    // the typed values; closing the dialog drops the question.
    $_PENDING_EXISTS: {
      on: {
        ON_RESEND_PENDING: { target: '$_RESEND_PENDING', actions: ['loadingTrue'] },
        ON_CANCEL_PENDING: { target: '$_FORM', actions: ['clearPendingRef'] },
        ON_CLOSE_MODAL: {
          target: '$_VIEW',
          actions: ['handleModal', 'clearPendingRef'],
        },
      },
    },
    $_RESEND_PENDING: {
      // The dialog cannot be dismissed mid-send: the in-flight resend must
      // reach onDone/onError so `loading` is cleared.
      on: { ON_CLOSE_MODAL: {} },
      invoke: {
        id: 'resendPending',
        src: 'resendReference',
        input: ({ context }) => ({ id: context.pendingRef?.id ?? -1 }),
        onDone: {
          target: '$_LOAD',
          actions: ['handleModal', 'clearPendingRef', 'showResentAlert'],
        },
        onError: {
          target: '$_VIEW',
          actions: ['handleModal', 'clearPendingRef', 'loadingFalse', 'sendError'],
        },
      },
    },
    // [MCV-1838] Re-send a pending request from its grid row (same link), then
    // reload so the list reflects the server.
    $_RESEND_ROW: {
      invoke: {
        id: 'resendRow',
        src: 'resendReference',
        input: ({ event }) => ({ id: (event as OnResendReference).payload.id }),
        onDone: {
          target: '$_LOAD',
          actions: ['showResentAlert'],
        },
        onError: {
          target: '$_VIEW',
          actions: ['loadingFalse', 'sendError'],
        },
      },
    },
    $_LOAD: {
      invoke: {
        id: 'getRefs',
        src: 'getReferences',
        input: (scope: Scope) => scope as { context: Context; event: OnGetReferences },
        onDone: {
          target: '$_VIEW',
          actions: ['populateRefs', 'loadingFalse'],
        },
        onError: {
          target: '$_VIEW',
          actions: ['sendError', 'loadingFalse'],
        },
      },
    },
    $_DOWNLOAD_PDF: {
      invoke: {
        id: 'getRef',
        src: 'getPDF',
        input: (scope: Scope) => scope as { context: Context; event: OnGetReferencePDF },
        onDone: {
          target: '$_VIEW',
          actions: ['loadPDF', 'loadingFalse'],
        },
        onError: {
          target: '$_VIEW',
          actions: ['sendError', 'loadingFalse'],
        },
      },
    },
    $_DELETE: {
      invoke: {
        id: 'deleteRef',
        src: 'deleteReference',
        input: (scope: Scope) => scope as { context: Context; event: OnDeleteRequest },
        onDone: {
          target: '$_LOAD',
        },
        onError: {
          target: '$_VIEW',
          actions: ['sendError', 'loadingFalse'],
        },
      },
    },
  },
});
